import Vue from 'vue';
import {i18n} from '@/main';
import VueRouter from 'vue-router';
import Router from '@/router/Router';

import Admin from '@/views/Admin';
import Setup from '@/views/Setup';
import ResetPassword from '@/views/ResetPassword';

import setupCompletedMiddleware from '@/middlewares/setupCompleted';
import setupUncompletedMiddleware from '@/middlewares/setupUncompleted';
import authMiddleware from '@/middlewares/auth';
import galleriesMiddleware from '@/middlewares/galleries';
import Login from '@/views/Login';
import ResetPasswordConfirm from '@/views/ResetPasswordConfirm';
import Lang from '@/views/Lang.vue';
import Gallery from '@/views/Gallery.vue';
import Galleries from '@/views/Galleries.vue';
import Start from '@/views/Start.vue';
import Transfer from '../views/Transfer.vue';
import Order from '@/views/Order.vue';
import ClickAndBuys from '@/views/clickandbuy/ClickAndBuys.vue';
import ClickAndBuy from '@/views/clickandbuy/ClickAndBuy.vue';
import SwifterUpload from '@/views/swifter/SwifterUpload.vue';
import TransferChannel from '@/views/TransferChannel.vue';

Vue.use(VueRouter);

const navigatorLanguages = [
    {
        key: 'de',
        codes: ['de', 'de-*'],
    },
    {
        key: 'ch',
        codes: ['ch', 'ch-*'],
    },
    {
        key: 'at',
        codes: ['at', 'at-*'],
    },
    {
        key: 'li',
        codes: ['li', 'li-*'],
    },
    {
        key: 'en',
        codes: ['en', 'en-*'],
    },
    {
        key: 'it',
        codes: ['it', 'it-*'],
    },
    {
        key: 'fr',
        codes: ['fr', 'fr-*'],
    },
    {
        key: 'nl',
        codes: ['nl', 'nl-*'],
    },
    {
        key: 'fi',
        codes: ['fi', 'fi-*'],
    },
    {
        key: 'tw',
        codes: ['tw', 'tw-*'],
    },
];

const allowedLanguages = [
    'de',
    'ch',
    'at',
    'li',
    'en',
    'it',
    'fr',
    'nl',
    'fi',
    'tw',
];

function nextFactory(context, middleware, index) {
    const subsequentMiddleware = middleware[index];
    if (!subsequentMiddleware) return context.next;

    return (...parameters) => {
        context.next(...parameters);
        const nextMiddleware = nextFactory(context, middleware, index + 1);
        subsequentMiddleware({...context, next: nextMiddleware});
    };
}

const routes = [
    {
        path: '/:lang',
        component: Router,
        beforeEnter(to, from, next) {
            let lang = to.params.lang;
            let target = lang;

            lang = Vue.localStorage.get('language');

            if (!allowedLanguages.includes(lang)) {
                for (let i = 0, j = navigatorLanguages.length; i < j; i++) {
                    let navigatorLanguage = navigator.language;
                    navigatorLanguage = navigatorLanguage.toLowerCase();

                    let wildcard = navigatorLanguage.substring(0, 3) + '*';

                    if (
                        navigatorLanguages[i].codes.includes(
                            navigatorLanguage
                        ) ||
                        navigatorLanguages[i].codes.includes(wildcard)
                    ) {
                        lang = navigatorLanguages[i].key;
                        break;
                    }
                }
            }

            if (!allowedLanguages.includes(lang)) {
                lang = 'de';
            }

            if (i18n.locale !== lang) {
                i18n.locale = lang;
            }

            Vue.localStorage.set('language', lang);

            if (!allowedLanguages.includes(target)) {
                router.push('/' + lang + '/' + target);
            }

            return next();
        },
        children: [
            {
                path: '',
                name: 'start',
                component: Start,
                meta: {
                    middleware: setupCompletedMiddleware,
                },
            },
            {
                path: 'admin',
                name: 'admin',
                component: Admin,
                meta: {
                    middleware: [setupCompletedMiddleware, authMiddleware],
                },
            },
            {
                path: 'login',
                name: 'login',
                component: Login,
                meta: {
                    middleware: setupCompletedMiddleware,
                },
            },
            {
                path: 'setup',
                name: 'setup',
                component: Setup,
                meta: {
                    middleware: setupUncompletedMiddleware,
                },
            },
            {
                path: 'reset-password',
                name: 'reset-password',
                component: ResetPassword,
                meta: {
                    middleware: setupCompletedMiddleware,
                },
            },
            {
                path: 'reset-password-confirm',
                name: 'reset-password-confirm',
                component: ResetPasswordConfirm,
                meta: {
                    middleware: setupCompletedMiddleware,
                },
            },

            {
                path: 'galleries',
                name: 'galleries',
                component: Galleries,
                meta: {
                    middleware: [setupCompletedMiddleware, galleriesMiddleware],
                },
            },
            {
                path: 'galleries/:gallery',
                name: 'gallery',
                component: Gallery,
                meta: {
                    middleware: [setupCompletedMiddleware, galleriesMiddleware],
                },
            },

            {
                path: 'transfers',
                name: 'transferChannel',
                component: TransferChannel,
                meta: {
                    middleware: [setupCompletedMiddleware],
                },
            },
            {
                path: 'transfers/:transfer',
                name: 'transfer',
                component: Transfer,
                meta: {
                    middleware: [setupCompletedMiddleware],
                },
            },
            {
                path: 'swifts',
                name: 'swiftsChannel',
                component: TransferChannel,
                meta: {
                    middleware: [setupCompletedMiddleware],
                },
            },
            {
                path: 'swifts/:transfer',
                name: 'swiftChannel',
                component: Transfer,
                meta: {
                    middleware: [setupCompletedMiddleware],
                },
            },
            {
                path: 'swift',
                name: 'swift',
                component: SwifterUpload,
                meta: {
                    middleware: [setupCompletedMiddleware],
                },
            },

            {
                path: 'clickandbuys',
                redirect: 'lunacart',
            },
            {
                path: 'clickandbuys/:clickandbuy',
                redirect: 'lunacart/:clickandbuy',
            },
            {
                path: 'clickandbuys/:clickandbuy/orders/:order',
                redirect: 'lunacart/:clickandbuy/orders/:order',
            },
            {
                path: 'lunacart',
                name: 'clickandbuys',
                component: ClickAndBuys,
                meta: {
                    middleware: [setupCompletedMiddleware],
                },
            },
            {
                path: 'lunacart/:clickandbuy',
                name: 'clickandbuy',
                component: ClickAndBuy,
                meta: {
                    middleware: [setupCompletedMiddleware],
                },
            },
            {
                path: 'lunacart/:clickandbuy/orders/:order',
                name: 'clickandbuyOrder',
                component: ClickAndBuy,
                meta: {
                    middleware: [setupCompletedMiddleware],
                },
            },
            {
                path: 'lunacart/:clickandbuy/files',
                name: 'clickandbuyFiles',
                component: ClickAndBuy,
                meta: {
                    middleware: [setupCompletedMiddleware],
                },
            },

            {
                path: 'orders/:order',
                name: 'order',
                component: Order,
                meta: {
                    middleware: [setupCompletedMiddleware],
                },
            },
            {
                path: 'galleries/:gallery/orders/:order',
                name: 'order',
                component: Order,
                meta: {
                    middleware: [setupCompletedMiddleware, galleriesMiddleware],
                },
            },

            /*
            {
                path: 'upload',
                name: 'upload',
                component: Main,
                meta: {
                    middleware: [setupCompletedMiddleware],
                },
            },
             */
        ],
    },
    {
        path: '*',
        name: 'lang',
        component: Lang,
        meta: {
            middleware: [setupCompletedMiddleware],
        },
    },
];

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
    scrollBehavior() {
        return {x: 0, y: 0};
    },
});

router.beforeEach((to, from, next) => {
    if (to.meta.middleware) {
        const middleware = Array.isArray(to.meta.middleware)
            ? to.meta.middleware
            : [to.meta.middleware];

        const context = {
            from,
            next,
            router,
            to,
        };
        const nextMiddleware = nextFactory(context, middleware, 1);

        return middleware[0]({...context, next: nextMiddleware});
    }

    return next();
});

export default router;
